import React from "react";
import { motion } from "framer-motion";
import TierBadge from "./TierBadge";
import {
  LeaderboardEntry,
  LINCOLN_ROAD_LEADERBOARD,
  DEFAULT_BOUTIQUE_NAME,
} from "../config/leaderboard";

// Nespresso brand colors
const NESPRESSO = {
  brown: "#986f38",
  gold: "#c69f36",
  black: "#000000",
  darkGray: "#292929",
};

export interface LeaderboardScreenProps {
  data?: LeaderboardEntry[];
  boutiqueName?: string;
}

const LeaderboardScreen: React.FC<LeaderboardScreenProps> = ({
  data = LINCOLN_ROAD_LEADERBOARD,
  boutiqueName = DEFAULT_BOUTIQUE_NAME,
}) => {
  const top3 = data.slice(0, 3);
  const rest = data.slice(3, 10);

  return (
    <div
      className="flex-1 flex flex-col px-8 py-6"
      style={{ backgroundColor: NESPRESSO.black }}
    >
      {/* Header */}
      <motion.div
        className="text-center mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <p
          className="text-sm uppercase tracking-widest mb-2 font-light"
          style={{ color: NESPRESSO.gold }}
        >
          {boutiqueName}
        </p>
        <h2 className="text-3xl font-light text-white">Top Recyclers</h2>
      </motion.div>

      {/* Top 3 Podium */}
      <motion.div
        className="flex justify-center items-end gap-4 mb-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        {/* 2nd Place */}
        {top3[1] && (
          <PodiumEntry entry={top3[1]} position={2} delay={0.3} />
        )}

        {/* 1st Place */}
        {top3[0] && (
          <PodiumEntry entry={top3[0]} position={1} delay={0.2} />
        )}

        {/* 3rd Place */}
        {top3[2] && (
          <PodiumEntry entry={top3[2]} position={3} delay={0.4} />
        )}
      </motion.div>

      {/* Rest of Leaderboard */}
      <motion.div
        className="flex-1 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <div className="space-y-2">
          {rest.map((entry, index) => (
            <LeaderboardRow
              key={entry.rank}
              entry={entry}
              delay={0.5 + index * 0.05}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

// Podium entry for top 3
const PodiumEntry: React.FC<{
  entry: LeaderboardEntry;
  position: 1 | 2 | 3;
  delay: number;
}> = ({ entry, position, delay }) => {
  const heights = { 1: "h-32", 2: "h-24", 3: "h-20" };
  const widths = { 1: "w-28", 2: "w-24", 3: "w-24" };

  const positionColors = {
    1: NESPRESSO.gold,
    2: "#C0C0C0",
    3: "#CD7F32",
  };

  return (
    <motion.div
      className={`${widths[position]} flex flex-col items-center`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4, ease: "easeOut" }}
    >
      {/* Badge */}
      <div className="mb-2">
        <TierBadge tier={entry.tier} size={position === 1 ? "lg" : "md"} />
      </div>

      {/* Name */}
      <p
        className={`text-white font-medium text-center mb-1 ${
          position === 1 ? "text-base" : "text-sm"
        }`}
      >
        {entry.name}
      </p>

      {/* Pods */}
      <p className="text-white/60 text-xs mb-2">
        {entry.podsReturned.toLocaleString()} pods
      </p>

      {/* Podium block */}
      <div
        className={`${widths[position]} ${heights[position]} rounded-t-lg flex items-center justify-center`}
        style={{
          background: `linear-gradient(180deg, ${positionColors[position]}40 0%, ${positionColors[position]}20 100%)`,
          borderTop: `2px solid ${positionColors[position]}`,
          borderLeft: `1px solid ${positionColors[position]}40`,
          borderRight: `1px solid ${positionColors[position]}40`,
        }}
      >
        <span
          className="text-3xl font-light"
          style={{ color: positionColors[position] }}
        >
          {position}
        </span>
      </div>
    </motion.div>
  );
};

// Leaderboard row for positions 4-10
const LeaderboardRow: React.FC<{
  entry: LeaderboardEntry;
  delay: number;
}> = ({ entry, delay }) => {
  return (
    <motion.div
      className="flex items-center gap-4 px-4 py-3 rounded-lg"
      style={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.3 }}
    >
      {/* Rank */}
      <span className="w-6 text-white/40 text-sm font-medium">
        {entry.rank}
      </span>

      {/* Badge */}
      <TierBadge tier={entry.tier} size="sm" />

      {/* Name */}
      <span className="flex-1 text-white text-sm">{entry.name}</span>

      {/* Pods */}
      <span className="text-white/60 text-sm">
        {entry.podsReturned.toLocaleString()}
      </span>
    </motion.div>
  );
};

export default LeaderboardScreen;
